// 「どこで買ったものが安かったか」を調べる画面
//
// レシート単位ではなく明細単位で探す。検索語を入れると、
// 上に店ごとの単価比較、下にヒットした明細が並ぶ。
import { useState } from 'react'

import type { PriceBasis, ReceiptItemSortKey } from '@/api/types/receipt'
import { Badge } from '@/components/ui/badge'
import { SearchBox } from '@/components/ui/search-box'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { cn } from '@/lib/utils'

import { usePriceSummary, useReceiptItemCategories, useReceiptItemSearch } from '../hooks/use-receipt-items'
import { PriceComparisonCard } from './price-comparison-card'
import { ReceiptItemRow } from './receipt-item-row'

type Props = {
  onSelectReceipt: (receiptId: number) => void
}

const SORT_OPTIONS: readonly { value: ReceiptItemSortKey; label: string }[] = [
  { value: 'purchased_at', label: '新しい順' },
  { value: 'unit_price', label: '安い順' },
]

const BASIS_OPTIONS: readonly { value: PriceBasis; label: string }[] = [
  { value: 'net', label: '実売' },
  { value: 'list', label: '定価' },
]

/** 安い順のときだけ昇順にしたいので、並べ替えの基準から向きを決める */
const orderFor = (sort: ReceiptItemSortKey) => (sort === 'unit_price' ? 'asc' : 'desc')

export const ItemSearchPanel = ({ onSelectReceipt }: Props) => {
  const [queryInput, setQueryInput] = useState('')
  const [category, setCategory] = useState<string | null>(null)
  const [sort, setSort] = useState<ReceiptItemSortKey>('purchased_at')
  const [basis, setBasis] = useState<PriceBasis>('net')

  const query = useDebouncedValue(queryInput)
  const hasQuery = query.trim().length > 0

  const { data: categories } = useReceiptItemCategories()
  const {
    data: items,
    isPending,
    isError,
    error,
  } = useReceiptItemSearch(
    { q: query || undefined, category: category ?? undefined, sort, order: orderFor(sort) },
    { enabled: hasQuery || category !== null },
  )
  // 商品名で絞っていないと、無関係なものを混ぜて平均を出すことになる
  const { data: summary } = usePriceSummary({ q: query, category: category ?? undefined }, { enabled: hasQuery })

  const isIdle = !hasQuery && category === null

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="shrink-0 px-4 pb-2">
        <SearchBox
          value={queryInput}
          onChange={(event) => setQueryInput(event.target.value)}
          placeholder="商品名で探す"
        />
      </div>

      {(categories?.length ?? 0) > 0 && (
        <div className="flex shrink-0 gap-1.5 overflow-x-auto px-4 pb-2">
          {categories?.map((value) => {
            const isActive = category === value
            return (
              <button
                key={value}
                type="button"
                aria-pressed={isActive}
                onClick={() => setCategory(isActive ? null : value)}
                className="shrink-0"
              >
                <Badge
                  variant="outline"
                  className={cn('cursor-pointer text-xs', isActive ? 'ring-1 ring-current' : 'opacity-50')}
                >
                  {value}
                </Badge>
              </button>
            )
          })}
        </div>
      )}

      <div className="flex shrink-0 items-center gap-3 px-4 pb-2 text-xs">
        <div className="flex gap-1">
          {SORT_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              aria-pressed={sort === option.value}
              onClick={() => setSort(option.value)}
              className={cn(
                'rounded px-2 py-1 transition-colors',
                sort === option.value ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:bg-white/5',
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
        <div className="ml-auto flex gap-1">
          {BASIS_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              aria-pressed={basis === option.value}
              onClick={() => setBasis(option.value)}
              className={cn(
                'rounded px-2 py-1 transition-colors',
                basis === option.value ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:bg-white/5',
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
        {isIdle && <p className="text-sm text-muted-foreground">商品名を入れると、店ごとの値段を比べられます。</p>}

        {isError && (
          <div className="rounded border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-500">
            {error instanceof Error ? error.message : '読み込みに失敗しました。'}
          </div>
        )}

        {!isIdle && isPending && <p className="text-sm text-muted-foreground">読み込み中…</p>}

        {!isIdle && !isPending && !isError && (
          <div className="flex flex-col gap-3">
            {summary && summary.stores.length > 1 && <PriceComparisonCard summary={summary} basis={basis} />}

            {items?.length === 0 && <p className="text-sm text-muted-foreground">該当する買い物はありません。</p>}

            <div className="flex flex-col gap-2">
              {items?.map((item) => (
                <ReceiptItemRow key={item.id} item={item} basis={basis} onSelectReceipt={onSelectReceipt} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
